import {$s, $t} from '@bitstillery/common/app'
import {slugify} from '@bitstillery/common/lib/utils'
import {getSSRContext} from '@bitstillery/mithril'

/**
 * A CMS page owns its hero copy under two page-bound keys — `page.title` and `page.description` —
 * that are the same on every page and bound to the page row, so renaming the page's path no longer
 * orphans its title. Surfaces that have no CMS page behind them (the shop, my-portal, the cart) keep
 * slug-keyed *global* copy under `shell.*`, since there is no page row to bind to.
 */
export const PAGE_TITLE_KEY = 'page.title'
export const PAGE_DESCRIPTION_KEY = 'page.description'

/**
 * Which key family the surface on screen takes its title/description from:
 * - `cms` — a published CMS page, page-bound `page.title` / `page.description`
 * - `shell` — a route with no page row (shop, my-portal), slug-keyed `shell.*`
 * - `not_found` — no page at all, and so no copy: the 404 speaks through its status numeral (see
 *   `cms/components/not_found`, and its static twin in `error_page.ts`). This kind exists to *suppress*
 *   copy, not to select it — without it the additive i18next bundle would caption a 404 reached by
 *   client-side navigation with the previous page's title.
 */
export type PageCopyKind = 'cms' | 'shell' | 'not_found'

/** The shell key suffix for a non-CMS route: `/my-portal/orders/42` → `my_portal_orders`. */
function shell_slug(uri: string): string {
    const uri_without_ids = uri
        .split('/')
        .filter((segment) => segment !== '' && !/^\d+$/.test(segment))
        .join('/')
    return slugify(uri_without_ids) || 'home'
}

export interface PageCopy {
    title: string
    description: string
}

/**
 * Title + description for the surface currently on screen. `kind` decides which key family to read;
 * it must not be inferred from whether `page.title` resolves, because i18next bundles are additive —
 * the previous CMS page's `page.title` is still in the bundle after navigating to a shell route, and
 * would silently caption it.
 */
export function page_copy(kind: PageCopyKind): PageCopy {
    // i18next answers a missing key with the key itself; blank those out so a surface with no copy of
    // its own (e.g. /shop/checkout) shows nothing rather than `shell.title.shop_checkout`.
    const copy = (key: string, options?: Record<string, unknown>): string => {
        const value = options === undefined ? $t(key) : $t(key, options)
        return value === key ? '' : value
    }
    if (kind === 'not_found') {
        // Nothing to render and nothing to translate; `document_title` falls back to the vendor alone.
        return {description: '', title: ''}
    }
    if (kind === 'cms') {
        return {
            description: copy(PAGE_DESCRIPTION_KEY),
            title: copy(PAGE_TITLE_KEY, {vendor: process.env.MSI_TITLE}),
        }
    }
    const slug = shell_slug($s.env.uri)
    return {
        description: copy(`shell.description.${slug}`),
        title: copy(`shell.title.${slug}`, {vendor: process.env.MSI_TITLE}),
    }
}

/** `About us · Moving Spirits`, minus the suffix when the copy already names the vendor. */
function document_title(title: string): string {
    const vendor = process.env.MSI_TITLE
    if (!title) {
        return vendor
    }
    return title.includes(vendor) ? title : `${title} · ${vendor}`
}

/**
 * Publish the current page's copy as the document title + meta description.
 *
 * On the server this goes into the request's `ssrStateMeta.head`, which `server.ts` writes into the
 * template's `<head>` — a crawler that never runs the bundle still sees per-page metadata. In the
 * browser it patches the live document on every page commit.
 */
export function apply_document_meta(kind: PageCopyKind): void {
    const {title, description} = page_copy(kind)
    const head = {description, title: document_title(title)}

    if (globalThis.__SSR_MODE__) {
        const context = getSSRContext()
        if (context) {
            context.ssrStateMeta = {...context.ssrStateMeta, head}
        }
        return
    }

    // Called from the shell header's view, so this runs on every redraw; only touch the document when
    // the copy actually changed (a page commit, a language switch), not on every frame.
    if (document.title !== head.title) {
        document.title = head.title
    }
    const meta = document.querySelector('meta[name="description"]')
    if (meta && meta.getAttribute('content') !== head.description) {
        meta.setAttribute('content', head.description)
    }
}
